import React, { useState, useEffect } from 'react';
import { useAuthStore } from '@/store/authStore';
import { Shield, Mail, Send, CheckCircle2, AlertTriangle, Loader2, RefreshCcw, Gift, Save, IndianRupee, Brain } from 'lucide-react';
import ChangePasswordCard from '@/components/shared/ChangePasswordCard';
import { platformConfigAPI } from '@/services/paymentsAPI';

/** Small admin-only diagnostic card that reveals the exact reason Resend rejects emails.
 * Used to debug the "verification email not delivered" issue without SSH-ing into the box. */
function EmailDiagnosticsCard() {
  const [status, setStatus] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [testTo, setTestTo] = useState('');
  const [testResult, setTestResult] = useState(null);
  const [sending, setSending] = useState(false);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const fetchStatus = async () => {
    setLoadingStatus(true);
    try {
      const token = localStorage.getItem('auth_token');
      const r = await fetch(`${API_URL}/api/admin/email/status`, { headers: { Authorization: `Bearer ${token}` } });
      const d = await r.json();
      setStatus(d);
    } catch (e) {
      setStatus({ error: `Failed to fetch: ${e.message}` });
    }
    setLoadingStatus(false);
  };

  useEffect(() => { fetchStatus(); }, []);

  const sendTest = async () => {
    if (!testTo || !testTo.includes('@')) return;
    setSending(true);
    setTestResult(null);
    try {
      const token = localStorage.getItem('auth_token');
      const r = await fetch(`${API_URL}/api/admin/email/test-send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ to: testTo }),
      });
      const d = await r.json();
      setTestResult(d);
    } catch (e) {
      setTestResult({ sent: false, error: e.message });
    }
    setSending(false);
  };

  return (
    <div className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800/50 rounded-lg p-5" data-testid="email-diagnostics-card">
      <div className="flex items-center justify-between mb-1 gap-2">
        <h3 className="text-slate-900 dark:text-white font-medium text-sm flex items-center gap-2">
          <Mail className="w-4 h-4" /> Email Delivery (Resend)
        </h3>
        <button
          onClick={fetchStatus}
          disabled={loadingStatus}
          className="text-xs text-slate-500 dark:text-zinc-500 hover:text-emerald-600 inline-flex items-center gap-1"
          data-testid="refresh-email-status-btn"
        >
          <RefreshCcw className={`w-3 h-3 ${loadingStatus ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>
      <p className="text-slate-500 dark:text-zinc-500 text-xs mb-4">If verification emails aren&apos;t arriving, check status + send a test to see the exact Resend error.</p>

      {loadingStatus && !status && (
        <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-zinc-500"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…</div>
      )}

      {status && (
        <div className="space-y-2 text-xs">
          <Row label="API key configured" value={status.api_key_present ? `Yes (${status.api_key_prefix})` : 'No'} ok={!!status.api_key_present} />
          <Row label="Sender" value={status.sender} />
          {status.sender_domain && (
            <Row
              label="Sender domain"
              value={`${status.sender_domain} — ${status.sender_domain_verified ? 'verified' : 'NOT verified'}`}
              ok={!!status.sender_domain_verified}
            />
          )}
          {Array.isArray(status.domains) && status.domains.length > 0 && (
            <div className="text-slate-500 dark:text-zinc-500">
              Resend domains: {status.domains.map(d => `${d.name} (${d.status})`).join(', ')}
            </div>
          )}
          {status.error && (
            <div className="mt-2 px-3 py-2 rounded-md bg-red-500/10 border border-red-500/30 text-red-700 dark:text-red-400 flex items-start gap-2" data-testid="email-status-error">
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
              <span className="break-all">{status.error}</span>
            </div>
          )}
          {status.warning && !status.error && (
            <div className="mt-2 px-3 py-2 rounded-md bg-amber-500/10 border border-amber-500/30 text-amber-700 dark:text-amber-400 flex items-start gap-2" data-testid="email-status-warning">
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
              <span>{status.warning}</span>
            </div>
          )}
        </div>
      )}

      <div className="mt-5 pt-4 border-t border-slate-200 dark:border-zinc-800/50">
        <label className="block text-xs text-slate-500 dark:text-zinc-500 mb-1">Send test email</label>
        <div className="flex gap-2">
          <input
            type="email"
            placeholder="you@example.com"
            value={testTo}
            onChange={e => setTestTo(e.target.value)}
            className="flex-1 min-w-0 bg-slate-50 dark:bg-[#0a0a0b] border border-slate-200 dark:border-zinc-800 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-zinc-600"
            data-testid="email-test-to-input"
          />
          <button
            onClick={sendTest}
            disabled={sending || !testTo.includes('@')}
            className="px-3 py-2 rounded-lg text-xs font-medium bg-emerald-600 hover:bg-emerald-500 text-white disabled:opacity-50 inline-flex items-center gap-1.5"
            data-testid="email-test-send-btn"
          >
            <Send className="w-3.5 h-3.5" /> {sending ? 'Sending…' : 'Send'}
          </button>
        </div>
        {testResult && (
          <div className={`mt-2 px-3 py-2 rounded-md text-xs flex items-start gap-2 ${testResult.sent ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border border-emerald-500/30' : 'bg-red-500/10 text-red-700 dark:text-red-400 border border-red-500/30'}`} data-testid="email-test-result">
            {testResult.sent
              ? <><CheckCircle2 className="w-3.5 h-3.5 mt-0.5" /> <span>Delivered — id {testResult.id}</span></>
              : <><AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" /> <span className="break-all"><strong>{testResult.code || 'error'}:</strong> {testResult.error}</span></>
            }
          </div>
        )}
      </div>
    </div>
  );
}

function Row({ label, value, ok }) {
  return (
    <div className="flex justify-between gap-4">
      <span className="text-slate-500 dark:text-zinc-500 flex-shrink-0">{label}</span>
      <span className={`text-right break-all ${ok === true ? 'text-emerald-600 dark:text-emerald-400' : ok === false ? 'text-red-600 dark:text-red-400' : 'text-slate-600 dark:text-zinc-300'}`}>
        {value}
      </span>
    </div>
  );
}

/** Admin-configurable Mu Points rewards. Signup bonus, referral bonus, referral acceptance. */
function RewardsConfigCard() {
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const [cfg, setCfg] = useState(null);
  const [defaults, setDefaults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');

  const fetchCfg = async () => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('auth_token');
      const r = await fetch(`${API_URL}/api/admin/config/rewards`, { headers: { Authorization: `Bearer ${token}` } });
      const d = await r.json();
      if (!r.ok) throw new Error(d?.detail || 'Failed to load');
      setCfg({ ...d.config });
      setDefaults(d.defaults);
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  };

  useEffect(() => { fetchCfg(); }, []);

  const patch = (k, v) => setCfg((c) => ({ ...c, [k]: Math.max(0, parseInt(v || '0', 10) || 0) }));

  const save = async () => {
    if (!cfg) return;
    setSaving(true);
    setMsg('');
    setError('');
    try {
      const token = localStorage.getItem('auth_token');
      const r = await fetch(`${API_URL}/api/admin/config/rewards`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(cfg),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d?.detail || 'Save failed');
      setCfg({ ...d.config });
      setMsg('Rewards updated');
      setTimeout(() => setMsg(''), 2000);
    } catch (e) {
      setError(e.message);
    }
    setSaving(false);
  };

  const resetDefaults = () => {
    if (defaults) setCfg({ ...defaults });
  };

  return (
    <div className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800/50 rounded-lg p-5" data-testid="rewards-config-card">
      <div className="flex items-center justify-between mb-1 gap-2">
        <h3 className="text-slate-900 dark:text-white font-medium text-sm flex items-center gap-2">
          <Gift className="w-4 h-4" /> Rewards &amp; Bonuses
        </h3>
        <button
          onClick={fetchCfg}
          disabled={loading}
          className="text-xs text-slate-500 dark:text-zinc-500 hover:text-emerald-600 inline-flex items-center gap-1"
          data-testid="rewards-refresh"
        >
          {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCcw className="w-3 h-3" />} Refresh
        </button>
      </div>
      <p className="text-xs text-slate-500 dark:text-zinc-500 mb-4">
        Mu Points credited automatically at applicant signup. Changes apply to all future signups.
      </p>

      {loading || !cfg ? (
        <p className="text-xs text-slate-500 dark:text-zinc-500" data-testid="rewards-loading">Loading…</p>
      ) : (
        <div className="space-y-4">
          <RewardField
            label="Signup bonus"
            hint="Every new applicant receives this on registration."
            value={cfg.signup_bonus_mu}
            defaultValue={defaults?.signup_bonus_mu}
            onChange={(v) => patch('signup_bonus_mu', v)}
            testId="signup-bonus"
          />
          <RewardField
            label="Referral bonus"
            hint="Credited to the referrer when a new applicant signs up with their code."
            value={cfg.referral_bonus_mu}
            defaultValue={defaults?.referral_bonus_mu}
            onChange={(v) => patch('referral_bonus_mu', v)}
            testId="referral-bonus"
          />
          <RewardField
            label="Referral acceptance"
            hint="Extra bonus for the new applicant on top of the signup bonus when they used a code."
            value={cfg.referral_acceptance_mu}
            defaultValue={defaults?.referral_acceptance_mu}
            onChange={(v) => patch('referral_acceptance_mu', v)}
            testId="referral-acceptance"
          />
          <RewardField
            label="Daily login bonus"
            hint="Credited to an applicant the first time they log in each UTC day. Set to 0 to disable."
            value={cfg.daily_login_bonus_mu}
            defaultValue={defaults?.daily_login_bonus_mu}
            onChange={(v) => patch('daily_login_bonus_mu', v)}
            testId="daily-login-bonus"
          />

          <div className="flex items-center justify-between gap-3 pt-2">
            <button
              type="button"
              onClick={resetDefaults}
              className="text-xs text-slate-500 dark:text-zinc-500 hover:text-emerald-600"
              data-testid="rewards-reset"
            >
              Reset to defaults
            </button>
            <button
              type="button"
              onClick={save}
              disabled={saving}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium bg-emerald-600 hover:bg-emerald-500 text-white disabled:opacity-50"
              data-testid="rewards-save"
            >
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>

          {msg && <p className="text-xs text-emerald-600 dark:text-emerald-400" data-testid="rewards-success">{msg}</p>}
          {error && <p className="text-xs text-red-600 dark:text-red-400" data-testid="rewards-error">{error}</p>}
        </div>
      )}
    </div>
  );
}

function ConversionConfigCard() {
  const [cfg, setCfg] = useState({ inr_per_mu: 1 });
  const [defaults, setDefaults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const d = await platformConfigAPI.getConversion();
      setCfg(d.config); setDefaults(d.defaults);
    } catch (e) { setError('Failed to load'); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true); setMsg(''); setError('');
    try {
      const d = await platformConfigAPI.updateConversion(cfg.inr_per_mu);
      setCfg(d.config); setMsg('Conversion updated');
    } catch (e) { setError(e?.response?.data?.detail || 'Save failed'); }
    setSaving(false);
  };

  return (
    <div className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800/50 rounded-lg p-5" data-testid="conversion-config-card">
      <h3 className="text-slate-900 dark:text-white font-medium text-sm flex items-center gap-2 mb-1">
        <IndianRupee className="w-4 h-4 text-emerald-500" /> INR ↔ Mu Conversion
      </h3>
      <p className="text-xs text-slate-500 dark:text-zinc-500 mb-4">
        Sets how many rupees a user must pay for 1 Mu point. Default is 1:1 (₹1 = 1 Mu).
      </p>
      {loading ? (
        <p className="text-xs text-slate-500 dark:text-zinc-500">Loading…</p>
      ) : (
        <>
          <div className="flex items-center gap-2">
            <label className="text-sm text-slate-700 dark:text-zinc-300">₹</label>
            <input
              type="number" min="0.01" step="0.01"
              value={cfg.inr_per_mu}
              onChange={(e) => setCfg({ inr_per_mu: e.target.value })}
              className="w-32 bg-white dark:bg-[#0a0a0b] border border-slate-200 dark:border-zinc-800 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white focus:outline-none focus:border-emerald-500/40"
              data-testid="conversion-input"
            />
            <span className="text-sm text-slate-500 dark:text-zinc-500">= 1 Mu</span>
            {defaults && <span className="text-[10px] uppercase tracking-wider text-slate-400 dark:text-zinc-600 ml-2">default ₹{defaults.inr_per_mu}</span>}
          </div>
          <div className="mt-4 flex gap-2">
            <button
              onClick={save} disabled={saving}
              className="inline-flex items-center gap-1.5 px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg text-sm font-medium disabled:opacity-50"
              data-testid="conversion-save"
            >
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save
            </button>
          </div>
          {msg && <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-3" data-testid="conversion-success">{msg}</p>}
          {error && <p className="text-xs text-red-600 dark:text-red-400 mt-3" data-testid="conversion-error">{error}</p>}
        </>
      )}
    </div>
  );
}

function PrepConfigCard() {
  const [cfg, setCfg] = useState({ prep_question_mu: 2, prep_promo_active: true, prep_promo_message: '' });
  const [defaults, setDefaults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const d = await platformConfigAPI.getPrep();
      setCfg(d.config); setDefaults(d.defaults);
    } catch (e) { setError('Failed to load'); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true); setMsg(''); setError('');
    try {
      const d = await platformConfigAPI.updatePrep({
        prep_question_mu: Number(cfg.prep_question_mu),
        prep_promo_active: !!cfg.prep_promo_active,
        prep_promo_message: cfg.prep_promo_message,
      });
      setCfg(d.config); setMsg('Prep config updated');
    } catch (e) { setError(e?.response?.data?.detail || 'Save failed'); }
    setSaving(false);
  };

  return (
    <div className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800/50 rounded-lg p-5" data-testid="prep-config-card">
      <h3 className="text-slate-900 dark:text-white font-medium text-sm flex items-center gap-2 mb-1">
        <Brain className="w-4 h-4 text-amber-500" /> Interview Prep — Charging & Promo
      </h3>
      <p className="text-xs text-slate-500 dark:text-zinc-500 mb-4">
        Mu charged per interview-prep question (web + WhatsApp), and the limited-time banner shown to applicants.
      </p>
      {loading ? (
        <p className="text-xs text-slate-500 dark:text-zinc-500">Loading…</p>
      ) : (
        <>
          <div className="space-y-4">
            <div>
              <div className="flex items-center justify-between">
                <label className="text-sm text-slate-700 dark:text-zinc-300">Mu per question</label>
                {defaults && <span className="text-[10px] uppercase tracking-wider text-slate-400 dark:text-zinc-600">default {defaults.prep_question_mu}</span>}
              </div>
              <input
                type="number" min="0"
                value={cfg.prep_question_mu ?? 0}
                onChange={(e) => setCfg({ ...cfg, prep_question_mu: e.target.value })}
                className="mt-1 w-28 bg-white dark:bg-[#0a0a0b] border border-slate-200 dark:border-zinc-800 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white"
                data-testid="prep-question-mu-input"
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                id="prep-promo-toggle" type="checkbox"
                checked={!!cfg.prep_promo_active}
                onChange={(e) => setCfg({ ...cfg, prep_promo_active: e.target.checked })}
                className="w-4 h-4 rounded border-slate-300 dark:border-zinc-700"
                data-testid="prep-promo-toggle"
              />
              <label htmlFor="prep-promo-toggle" className="text-sm text-slate-700 dark:text-zinc-300">Show promo banner on applicant dashboard</label>
            </div>
            <div>
              <label className="text-sm text-slate-700 dark:text-zinc-300">Promo banner message</label>
              <input
                type="text"
                value={cfg.prep_promo_message || ''}
                onChange={(e) => setCfg({ ...cfg, prep_promo_message: e.target.value })}
                maxLength={280}
                className="mt-1 w-full bg-white dark:bg-[#0a0a0b] border border-slate-200 dark:border-zinc-800 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white"
                placeholder="Start preparing for interviews with as low as ₹2 — limited time!"
                data-testid="prep-promo-msg-input"
              />
              <p className="text-[11px] text-slate-500 dark:text-zinc-500 mt-1">
                Shown as a top toast for 15 seconds when an applicant lands on their dashboard (once per session).
              </p>
            </div>
          </div>
          <div className="mt-5 flex gap-2">
            <button
              onClick={save} disabled={saving}
              className="inline-flex items-center gap-1.5 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg text-sm font-medium disabled:opacity-50"
              data-testid="prep-config-save"
            >
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save
            </button>
          </div>
          {msg && <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-3" data-testid="prep-config-success">{msg}</p>}
          {error && <p className="text-xs text-red-600 dark:text-red-400 mt-3" data-testid="prep-config-error">{error}</p>}
        </>
      )}
    </div>
  );
}

function RewardField({ label, hint, value, defaultValue, onChange, testId }) {
  return (
    <div data-testid={testId}>
      <div className="flex items-center justify-between">
        <label className="text-sm text-slate-700 dark:text-zinc-300">{label}</label>
        {defaultValue !== undefined && (
          <span className="text-[10px] uppercase tracking-wider text-slate-400 dark:text-zinc-600">default {defaultValue}</span>
        )}
      </div>
      <div className="mt-1 flex items-center gap-2">
        <input
          type="number"
          min="0"
          value={value ?? 0}
          onChange={(e) => onChange(e.target.value)}
          className="w-28 bg-white dark:bg-[#0a0a0b] border border-slate-200 dark:border-zinc-800 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white focus:outline-none focus:border-emerald-500/40"
          data-testid={`${testId}-input`}
        />
        <span className="text-xs text-slate-500 dark:text-zinc-500">Mu</span>
      </div>
      <p className="text-[11px] text-slate-500 dark:text-zinc-500 mt-1">{hint}</p>
    </div>
  );
}

export default function AdminSettings() {
  const { user } = useAuthStore();
  return (
    <div data-testid="admin-settings">
      <h1 className="text-2xl font-semibold text-slate-900 dark:text-white mb-1">Settings</h1>
      <p className="text-slate-500 dark:text-zinc-500 text-sm mb-6">Admin account settings</p>

      <div className="space-y-6 max-w-lg">
        <div className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800/50 rounded-lg p-5">
          <h3 className="text-slate-900 dark:text-white font-medium text-sm flex items-center gap-2 mb-4">
            <Shield className="w-4 h-4" /> Account Info
          </h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-slate-500 dark:text-zinc-500">Email</span><span className="text-slate-600 dark:text-zinc-300">{user?.email}</span></div>
            <div className="flex justify-between"><span className="text-slate-500 dark:text-zinc-500">Role</span><span className="text-amber-600 dark:text-amber-400 capitalize">{user?.role}</span></div>
          </div>
        </div>
        <EmailDiagnosticsCard />
        <RewardsConfigCard />
        <ConversionConfigCard />
        <PrepConfigCard />
        <ChangePasswordCard testIdPrefix="admin-pw" />
      </div>
    </div>
  );
}
